import React, { useEffect, useState } from 'react';
import Page from "components/page/Page";
import { useHistory, useLocation } from 'react-router';
import { t } from 'i18next';
import DataTable from 'components/dataTable/DataTable';
import { DeleteCampaign, GetAudiences, GetEmailCampaigns, IAudience, IEmailCampaign, PostCampaignDuplicate, PostCampaignUnpublish, PostEmailCampaign, PutEmailMarketingSettings } from 'api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns';
import { StatusCode } from 'api/protocols';
import Card from 'components/card/Card';
import moment from 'moment';
import classNames from 'classnames';
import { ButtonNew } from 'components/buttonNew';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faPencil, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useDispatch } from 'react-redux';
import { showError, showSuccess } from 'redux/actions/ui';
import { Badge } from 'components/badge/Badge';
import _ from 'lodash';
import { faGear } from '@fortawesome/pro-solid-svg-icons';
import { NavigationDropdownNew } from 'components/navigationDropdownNew/NavigationDropdownNew';
import Toggle from 'components/form/toggle/Toggle';
import Checkbox from 'components/checkbox/Checkbox';
import { useAppSelector } from 'hooks/redux';
import { loadAdminFacility } from 'redux/actions/facility';
import EmailBuilderTemplateLibrary from './emailBuilder/templateLibrary/EmailBuilderTemplateLibrary';
import {
  emailMarketingCampaignsPath,
  emailMarketingSettingsTabPath,
  emailMarketingSmartListsTabPath,
  emailTemplateLibraryPath,
  isEmailMarketingTabPath,
} from './emailBuilder/emailBuilderWorkspace';
import { canUnpublishEmailCampaign } from './emailCampaigns/emailCampaignDesignStatus';
import { EmailCampaignUnpublishConfirmModal } from './emailCampaigns/EmailCampaignUnpublishConfirmModal';
import EmailCampaignBadge from './emailCampaigns/EmailCampaignBadge';

interface IPageState {
  campaigns: IEmailCampaign[];
  pending: boolean;
}

interface ISettings {
  allow_opt_ins: boolean;
  booking_page_checkbox: boolean;
  sign_up_button: boolean;
}

enum TabType {
  Campaign = 0,
  Templates,
  SmartList,
  Settings,
};

interface ICampaignsTabProps {
  campaigns: IEmailCampaign[];
  pending: boolean;
  onCreateCampaign: () => void;
  onDuplicateCampaign: (campaign: IEmailCampaign) => void;
  onUnpublishCampaign: (campaign: IEmailCampaign) => void;
  onDeleteCampaign: (campaign: IEmailCampaign) => void;
}

interface ISmartListsTabProps {
  audiences: IAudience[];
  pending: boolean;
}

interface ISettingsTabProps {
  settings: ISettings;
  setSettings: React.Dispatch<React.SetStateAction<ISettings>>;
  saving: boolean;
}

function resolveEmailMarketingTabFromPath(pathname: string): TabType {
  if (pathname === emailTemplateLibraryPath()) {
    return TabType.Templates;
  }

  if (pathname === emailMarketingSmartListsTabPath()) {
    return TabType.SmartList;
  }

  if (pathname === emailMarketingSettingsTabPath()) {
    return TabType.Settings;
  }

  return TabType.Campaign;
}

function emailMarketingTabPath(tab: TabType): string {
  if (tab === TabType.Templates) {
    return emailTemplateLibraryPath();
  }

  if (tab === TabType.SmartList) {
    return emailMarketingSmartListsTabPath();
  }

  if (tab === TabType.Settings) {
    return emailMarketingSettingsTabPath();
  }

  return emailMarketingCampaignsPath();
}

export default function EmailMarketing() {
  const { facilityStore } = useAppSelector(store => store);
  const permissions = facilityStore.facility?.permissions;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [state, setState] = useState<IPageState>({
    campaigns: [],
    pending: true,
  });
  const [selectedTab, setSelectedTab] = useState<TabType>(() =>
    resolveEmailMarketingTabFromPath(location.pathname),
  );
  const [templatesLibraryMounted, setTemplatesLibraryMounted] = useState(
    () => location.pathname === emailTemplateLibraryPath(),
  );
  const [audiences, setAudiences] = useState<IAudience[]>([]);
  const [settings, setSettings] = useState<ISettings>({
    allow_opt_ins: permissions?.email_marketing_allow_opt_ins ?? false,
    booking_page_checkbox: permissions?.email_marketing_booking_page_checkbox ?? false,
    sign_up_button: permissions?.email_marketing_sign_up_button ?? false,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [unpublishCampaign, setUnpublishCampaign] = useState<IEmailCampaign | null>(null);
  const [unpublishConfirming, setUnpublishConfirming] = useState(false);

  async function loadCampaigns() {
    const resp = await GetEmailCampaigns({}, false);
    if (resp.status === StatusCode.OK) {
      setState(prev => ({ ...prev, pending: false, campaigns: resp.data }));
    } else {
      setState(prev => ({ ...prev, pending: false }));
    }
  }

  async function loadAudiences() {
    const resp = await GetAudiences({}, false);
    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    setAudiences(resp.data);
  }

  async function putSettings() {
    setSavingSettings(true);
    const resp = await PutEmailMarketingSettings(settings, false);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }
    setSavingSettings(false);

    dispatch(showSuccess('Email marketing settings updated.'));

    dispatch(loadAdminFacility({ client: true }, false));
  }

  useEffect(() => {
    if (facilityStore.isLoaded && (
      permissions?.email_marketing_allow_opt_ins !== settings.allow_opt_ins ||
      permissions?.email_marketing_booking_page_checkbox !== settings.booking_page_checkbox ||
      permissions?.email_marketing_sign_up_button !== settings.sign_up_button
    )) {
      void putSettings();
    }
  }, [settings]);

  useEffect(() => {
    void loadCampaigns();
    void loadAudiences();
  }, []);

  useEffect(() => {
    setSettings({
      allow_opt_ins: permissions?.email_marketing_allow_opt_ins ?? false,
      booking_page_checkbox: permissions?.email_marketing_booking_page_checkbox ?? false,
      sign_up_button: permissions?.email_marketing_sign_up_button ?? false,
    });
  }, [permissions]);

  useEffect(() => {
    if (!isEmailMarketingTabPath(location.pathname)) {
      return;
    }

    const tab = resolveEmailMarketingTabFromPath(location.pathname);
    setSelectedTab(tab);

    if (tab === TabType.Templates) {
      setTemplatesLibraryMounted(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (selectedTab === TabType.Templates) {
      setTemplatesLibraryMounted(true);
    }
  }, [selectedTab]);

  const tabs = [
    {
      id: TabType.Campaign,
      content: t('Campaigns'),
    },
    {
      id: TabType.Templates,
      content: t('Templates'),
    },
    {
      id: TabType.SmartList,
      content: t('Smart Lists'),
    },
    {
      id: TabType.Settings,
      content: t('Settings'),
    },
  ];

  function selectTab(tab: TabType) {
    const path = emailMarketingTabPath(tab);

    if (location.pathname !== path) {
      history.push(path);
    }

    setSelectedTab(tab);
  }

  async function createCampaign() {
    const resp = await PostEmailCampaign({}, true);
    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    history.push('/admin/email-marketing/campaigns/' + resp.data.data.id + '/new');
  }

  async function duplicate(campaign: IEmailCampaign) {
    const resp = await PostCampaignDuplicate({ id: campaign.id });

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    dispatch(showSuccess(t('Duplicated email campaign')));
    history.push('/admin/email-marketing/campaigns/' + resp.data.data.id);
  }

  async function deleteCampaign(campaign: IEmailCampaign) {
    const resp = await DeleteCampaign({ id: campaign.id });

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    dispatch(showSuccess(t('Deleted email campaign')));
    void loadCampaigns();
  }

  async function confirmUnpublish() {
    if (unpublishCampaign == null || unpublishConfirming) {
      return;
    }

    setUnpublishConfirming(true);

    try {
      const resp = await PostCampaignUnpublish({ id: unpublishCampaign.id }, true);

      if (resp.status !== StatusCode.OK) {
        dispatch(showError(resp.message));
        return;
      }

      dispatch(showSuccess(resp.message || t('Campaign unpublished')));
      setUnpublishCampaign(null);
      void loadCampaigns();
    } finally {
      setUnpublishConfirming(false);
    }
  }

  return <Page
    title={t('Email Marketing')}
    subtitle={t('Engage your customers with email marketing campaigns.')}
    narrow
  >
    <div className="flex mb-4 overflow-x-auto">
      {tabs.map(tab =>
        <div
          key={tab.id}
          className={classNames('px-4 py-2 flex-1 min-w-[120px] text-center cursor-pointer whitespace-nowrap', {
            'border-b-2 text-gray-900': tab.id === selectedTab,
            'border-b border-gray-200 text-gray-500': tab.id !== selectedTab,
          })}
          onClick={() => selectTab(tab.id)}>
          {tab.content}
        </div>,
      )}
    </div>
    <Card>
      <Card.Section>
        {selectedTab === TabType.Campaign && <CampaignsTab
          campaigns={state.campaigns}
          pending={state.pending}
          onCreateCampaign={createCampaign}
          onDuplicateCampaign={duplicate}
          onUnpublishCampaign={setUnpublishCampaign}
          onDeleteCampaign={deleteCampaign}
        />}
        {templatesLibraryMounted ? (
          <div
            className="**:[.ui-card]:!overflow-visible"
            hidden={selectedTab !== TabType.Templates}
            aria-hidden={selectedTab !== TabType.Templates}
          >
            <EmailBuilderTemplateLibrary />
          </div>
        ) : null}
        {selectedTab === TabType.SmartList && <SmartListsTab
          audiences={audiences}
          pending={state.pending}
        />}
        {selectedTab === TabType.Settings && <SettingsTab settings={settings} setSettings={setSettings} saving={savingSettings} />}
      </Card.Section>
    </Card>
    <EmailCampaignUnpublishConfirmModal
      open={unpublishCampaign != null}
      confirming={unpublishConfirming}
      scheduledType={unpublishCampaign?.scheduled_type}
      onClose={() => {
        if (!unpublishConfirming) {
          setUnpublishCampaign(null);
        }
      }}
      onConfirm={() => {
        void confirmUnpublish();
      }}
    />
  </Page>
    ;
}

function CampaignsTab(props: ICampaignsTabProps) {
  const history = useHistory();
  const {
    campaigns,
    pending,
    onCreateCampaign,
    onDuplicateCampaign,
    onUnpublishCampaign,
    onDeleteCampaign,
  } = props;

  if (!pending && campaigns.length === 0) {
    return <div className="flex flex-col items-center py-24 gap-2">
      <FontAwesomeIcon icon={faCircleCheck} />
      <p className="font-semibold">{t('Start building your email campaign')}</p>
      <p className="text-sm text-center">{t('Ready to bring your emails to life? Start by choosing a template and customize it to fit your vision.')}</p>
      <ButtonNew type="secondary" onClick={onCreateCampaign}>
        <FontAwesomeIcon icon={faPlus} className="mr-2" />
        {t('Get Started')}
      </ButtonNew>
    </div>;
  }

  function campaignStepUrl(campaign: IEmailCampaign) {
    let url = `/admin/email-marketing/campaigns/${campaign.id}`;
    if (campaign.status !== 'draft') {
      return history.push(url);
    }

    let step = 1;
    if (campaign.description !== '') {
      step += 1;
    }

    if ((campaign?.audiences_count ?? 0) > 0
      && campaign.subject !== ''
      && campaign.text_preview !== ''
      && campaign.from_name !== ''
      && campaign.from_address !== ''
      && !campaign.design?.has_draft // If need to select template still
    ) {
      step += 1;
    }

    // If has published an needs scheduling
    if (campaign.design?.active_published_design_id !== null
      && campaign.design?.has_unpublished_changes === false 
      && campaign?.design.has_draft === true
    ) {
      step += 2;
    }
    url += `/new?step=${step}`;

    return history.push(url);
  }

  return <>
    <div className="pb-6 flex justify-between items-end">
      <div className="space-y-2">
        <div className="font-medium">{t('My campaigns')}</div>
        <p>{t('Create and manage campaigns for all your emails.')}</p>
      </div>
      <div>
        <ButtonNew size="medium" type="primary" onClick={onCreateCampaign}>
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          {t('Create Campaign')}
        </ButtonNew>
      </div>
    </div>
    <div className="**:[.ui-card,.data-table]:!overflow-visible">
      <DataTable
        overflow
        columns={[
          { label: t("Name") },
          { label: t("Status") },
          { label: t("Description") },
          { label: t("Last Modified") },
          { label: t("") },
        ]}
        loading={pending}>
        {campaigns.map((campaign, index) => (
          <tr
            key={index}
            className="clickable"
            onClick={() => campaignStepUrl(campaign)}
          >
            <td>
              <div className="max-w-[200px] truncate" title={campaign.name}>{campaign.name}</div>
            </td>
            <td><EmailCampaignBadge campaign={campaign} /></td>
            <td>
              <div className="max-w-[200px] truncate" title={campaign.description}>{campaign.description}</div>
            </td>
            <td>{moment(campaign.updated_at).format('MMMM D, YYYY [at] h:mm A')}</td>
            <td
              align="right"
              className="relative overflow-visible"
              onClick={event => event.stopPropagation()}
            >
              <NavigationDropdownNew
                showPlainTextLabel
                rightAlign
                position={campaigns.length > 1 && index >= campaigns.length - 2 ? "top" : "bottom"}
                label={<FontAwesomeIcon icon={faGear} />}
                sections={[
                  [
                    {
                      label: t('Edit'),
                      icon: 'pencil',
                      onClick: () => history.push('/admin/email-marketing/campaigns/' + campaign.id),
                    },
                    ...(canUnpublishEmailCampaign(campaign.status)
                      ? [
                        {
                          label: t('Unpublish'),
                          icon: 'eye-slash' as const,
                          onClick: () => onUnpublishCampaign(campaign),
                        },
                      ]
                      : []),
                    {
                      label: t('Duplicate'),
                      icon: 'copy',
                      onClick: () => onDuplicateCampaign(campaign),
                    },
                    {
                      label: t('Remove'),
                      icon: 'trash',
                      onClick: () => onDeleteCampaign(campaign),
                    },
                  ],
                ]}
              >
              </NavigationDropdownNew>
            </td>
          </tr>
        ))}
      </DataTable>
    </div>
  </>;
}

function SmartListsTab(props: ISmartListsTabProps) {
  const history = useHistory();
  const {
    audiences,
    pending,
  } = props;

  return <>
    <div className="pb-6 flex justify-between items-end">
      <div className="mb-2">{t('Smart Lists')}</div>
      <div>
        <ButtonNew size="medium" type="primary" onClick={() => history.push('/admin/email-marketing/campaigns/smart-list/new?type=smart')}>
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          {t('Create Smart List')}
        </ButtonNew>
      </div>
    </div>
    <div className="overflow-x-auto **:[.ui-card,.data-table]:!overflow-visible">
      <DataTable
        columns={[
          { label: t("Name") },
          { label: t("Customer Count") },
          { label: t("Type") },
          { label: t("Last Modified") },
          { label: t("") },
        ]}
        loading={pending}
      >
        {audiences.map(audience =>
          <tr
            key={audience.title + audience.id}
            className="clickable"
            onClick={() => history.push(`/admin/email-marketing/campaigns/smart-list/${audience?.id}`)}
          >
            <td>{audience.title}</td>
            <td>{audience.included_customer_count}</td>
            <td>
              <Badge type={audience.type === 'smart' ? 'success' : undefined}>
                {t(_.startCase(audience.type ?? ''))}
              </Badge>
            </td>
            <td>{moment(audience.updated_at).format('MMMM D, YYYY [at] h:mm A')}</td>
            <td><FontAwesomeIcon icon={faPencil} /></td>
          </tr>)}
        {audiences.length < 1 && <tr>
          <td colSpan={5} className="text-center">{t('No smart lists have been created yet')}</td>
        </tr>}
      </DataTable>
    </div>
  </>;
}

function SettingsTab({ settings, setSettings, saving }: ISettingsTabProps) {

  return <>
    <div className="pb-4 flex justify-between items-end border-b border-gray-200">
      <div className="space-y-2">
        <div className="font-medium">{t('Email marketing opt-in')}</div>
      </div>
    </div>
    <div className="mt-4">
      <div className="flex gap-2">
        <Toggle
          checked={settings.allow_opt_ins}
          onChange={e => {
            const allowOptIns = e.target.checked;
            setSettings(prev => ({
              ...prev,
              allow_opt_ins: allowOptIns,
              ...(allowOptIns
                ? { booking_page_checkbox: true, sign_up_button: true }
                : {}),
            }));
          }}
          disabled={saving}
        />

        <div className="text-sm">
          <div className="font-medium">{t('Allow customer opt-ins')}</div>
          <div className="text-gray-700">{t('Choose where customers can opt-in from and help grow your mailing list.')}</div>
          <div className="ml-6 mt-2 flex gap-2 ">
            <Checkbox
              checked={settings.sign_up_button}
              onChange={e => setSettings(prev => ({ ...prev, sign_up_button: e.target.checked }))}
              disabled={!settings.allow_opt_ins || saving}
            />
            <div>
              <div className={classNames("font-medium",
                { 'text-gray-500': !settings.allow_opt_ins },
              )}>
                {t('From the booking page header')}
              </div>
            </div>
          </div>
          <div className="ml-6 mt-2 flex gap-2">
            <Checkbox
              checked={settings.booking_page_checkbox}
              onChange={e => setSettings(prev => ({ ...prev, booking_page_checkbox: e.target.checked }))}
              disabled={!settings.allow_opt_ins || saving}
            />
            <div>
              <div className={classNames("font-medium",
                { 'text-gray-500': !settings.allow_opt_ins },
              )}>
                {t('When confirming their booking')}
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </>;
}
